import { Button, Card, Dropdown } from 'react-bootstrap';
import PersonUpdateStatusModal from '../PersonUpdateStatusModal';
import React from 'react';
import { PersonStatusesHistory } from '../PersonStatusesHistory';
import { FormattedSnoozeConfiguration, isCounterparty } from '../../common/helper';
import { snoozeInternalApi } from '../../apis';
import { fetchPerson } from '../amlActions';
import { connect, ResolveThunks, useDispatch } from 'react-redux';
import { AppState } from '../../store/store';
import { getUserId } from '../../auth/authReducer';
import { DropdownWrapper } from '../../common/DropdownWrapper';
import { InternalApiPerson } from '../../api-internal';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../feedback/feedbackReducer';
import { ContentPreviewer } from '../../common/ContentPreviewer';

interface StatusProps {
  person: InternalApiPerson | null;
}

export const Status = ({ person, fetchPerson, userId }: Props) => {
  const dispatch = useDispatch();
  const removeConfiguration = async () => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: 'You are going to remove snooze configuration',
        confirmationText: 'Yes',
        cancelText: 'No',
        onConfirm: async () => {
          if (person && person.snoozeConfiguration) {
            await snoozeInternalApi.deleteSnoozeConfiguration({
              personId: person.id,
              internalApiSnoozeConfig: {
                ...person.snoozeConfiguration,
                note: person.snoozeConfiguration.note,
                createdBy: { id: userId || 0 },
              },
            });
            await fetchPerson(person.id);
            dispatch(showFeedback({ message: 'Snooze has been removed.', success: true }));
          } else {
            dispatch(showFeedback({ message: 'Person or configuration is not present.', variant: 'danger' }));
          }
        },
      })
    );
  };

  return (
    <Card body={false}>
      <Card.Body>
        <div className='row align-items-center'>
          <div className='col'>
            <h6 className='card-title text-uppercase text-muted mb-2'>
              {isCounterparty(person || undefined) ? 'Counterparty' : 'Customer'} status
            </h6>
            <span data-testid='person-status' className='h2 mb-0'>
              {person ? person.status : ''}
            </span>
          </div>
          <div className='col-auto'>
            <PersonUpdateStatusModal />
          </div>
          <div className='col-auto'>
            <DropdownWrapper align='end'>
              <Dropdown.Toggle data-testid='status-dropdown' as={Button} className='btn-white' id='actions-dropdown'>
                •••
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <PersonStatusesHistory personId={person ? person.id : ''} category={person?.category} />
                {person && person.snoozeConfiguration && (
                  <Button data-testid='resume-alerts' className='dropdown-item' onClick={() => removeConfiguration()}>
                    Resume alerts
                  </Button>
                )}
              </Dropdown.Menu>
            </DropdownWrapper>
          </div>
        </div>
        {person && person.snoozeConfiguration && (
          <div className='row'>
            <div data-testid='snooze-config' className='col snooze-config-info mt-2'>
              <ContentPreviewer>
                <FormattedSnoozeConfiguration snoozeConfig={person.snoozeConfiguration} />
                {person.snoozeConfiguration.note}
              </ContentPreviewer>
            </div>
          </div>
        )}
        {person && person.scenarioSnoozeConfigurations && person.scenarioSnoozeConfigurations.length > 0 && (
          <div className='row'>
            <div data-testid='snooze-config' className='col snooze-config-info mt-2 content-preview-prefix'>
              {`This customer has ${person.scenarioSnoozeConfigurations.length} scenario${
                person.scenarioSnoozeConfigurations.length > 1 ? 's' : ''
              } separately snoozed`}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const mapStateToProps = (state: AppState) => ({
  userId: getUserId(state),
});

const mapDispatchToProps = {
  fetchPerson,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & StatusProps;

export default connect(mapStateToProps, mapDispatchToProps)(Status);
